import { useSelector } from 'react-redux';
import { getCurrencySymbol, toFixedTrunc } from '../../../../client/Juno/helpers';
import PropTypes from 'prop-types';

const BuyConfirmation = ({ formData, quotationData, loadingRate }) => {
    const { balancesCrypto, balancesCurrencies } = useSelector(state => state.juno);
    const imageBaseUrl = useSelector(state => state.config.image_base_url);

    const fiatCurrencyIcon = balancesCurrencies.find(
        x => x.currencyShortName === formData.fiatCurrency?.currencyShortName,
    )?.currencyIcon;
    const cryptoIDIcon = balancesCrypto.find(
        x => x.currencyShortName === formData.cryptoID?.currencyShortName,
    )?.currencyIcon;

    const fiatCurrencyShortName = formData.fiatCurrency?.currencyShortName || formData.fiatCurrency;
    const cryptoCurrencyShortName = formData.cryptoID?.currencyShortName || formData.cryptoID;

    return (
        <div className="flex flex-col gap-9">
            <span className="text-sm font-medium text-balanceCard-manage uppercase">
                Please double-check the summary below, before making a purchase
            </span>
            <div className="flex flex-col gap-6">
                <div className="">
                    <p className="text-xs font-medium text-balanceCard-manage mb-2">YOU PAY</p>
                    <div className=" border !border-[#D1D1D6] dark:!border-[#51525C] bg-card-bg rounded-md p-3">
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                {fiatCurrencyIcon && (
                                    <img
                                        src={`${imageBaseUrl}${fiatCurrencyIcon}`}
                                        width={24}
                                        height={24}
                                        alt="cryptoIcon"
                                    />
                                )}
                                <span className="text-base font-medium !text-text">
                                    {fiatCurrencyShortName}
                                </span>
                            </div>
                            <p
                                className="text-xl font-medium !text-text"
                                data-e2e="you-pay-confirm-amount"
                                data-e2e-curren={fiatCurrencyShortName}>
                                {getCurrencySymbol(fiatCurrencyShortName)} {formData.fiatAmount} (
                                {fiatCurrencyShortName})
                            </p>
                        </div>
                        <hr className="!border-[#D1D1D6] dark:!border-[#51525C] h-[1px] my-2" />
                        <div className="flex justify-between items-center">
                            <p className="text-xs font-medium text-balanceCard-manage">BALANCE:</p>
                            <p className="text-xs font-normal text-balanceCard-manage">
                                {getCurrencySymbol(formData?.fiatCurrency?.currencyShortName)}{' '}
                                {toFixedTrunc(formData?.fiatCurrency?.balanceAmount, 2)}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="">
                    <p className="text-xs font-medium text-balanceCard-manage mb-2">YOU GET</p>
                    <div className=" border !border-[#D1D1D6] dark:!border-[#51525C] bg-card-bg rounded-md p-3">
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                {cryptoIDIcon && (
                                    <img
                                        src={`${imageBaseUrl}${cryptoIDIcon}`}
                                        width={24}
                                        height={24}
                                        alt="cryptoIcon"
                                    />
                                )}
                                <span className="text-base font-medium !text-text">
                                    {cryptoCurrencyShortName}
                                </span>
                            </div>
                            <p
                                className="text-xl font-medium !text-text"
                                data-e2e="you-get-confirm-amount"
                                data-e2e-curren={cryptoCurrencyShortName}>
                                {getCurrencySymbol(cryptoCurrencyShortName)} {formData.cryptoAmount}{' '}
                                ({cryptoCurrencyShortName})
                            </p>
                        </div>
                        <hr className="!border-[#D1D1D6] dark:!border-[#51525C] h-[1px] my-2" />
                        <div className="flex justify-between items-center">
                            <p className="text-xs font-medium text-balanceCard-manage">BALANCE:</p>
                            <p className="text-xs font-normal text-balanceCard-manage">
                                {getCurrencySymbol(formData?.cryptoID?.currencyShortName)}{' '}
                                {toFixedTrunc(formData?.cryptoID?.balanceAmount, 8)}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-center bg-card-bg py-3 px-4 rounded-lg mb-6 w-full h-[36px]">
                    <div className="flex items-center gap-2 text-sm">
                        {' '}
                        {loadingRate && (
                            <div className="animate-spin h-[18px] w-[18px] border-2 border-[#A0A0AB4D] rounded-full border-t-2 !border-t-primary" />
                        )}
                        <span className="text-[14px] font-Inter font-medium !text-text">
                            1 {cryptoCurrencyShortName} ={' '}
                            {quotationData ? toFixedTrunc(quotationData?.price, 2) : '?'}{' '}
                            {fiatCurrencyShortName}
                        </span>
                    </div>
                </div>
                {quotationData?.error && (
                    <div
                        className="alert alert-danger rounded-lg p-2 text-red-600 bg-red-100 mt-3"
                        role="alert">
                        Error while fetching quotation
                    </div>
                )}
            </div>
        </div>
    );
};

BuyConfirmation.propTypes = {
    formData: PropTypes.shape({
        fiatCurrency: PropTypes.shape({
            currencyShortName: PropTypes.string,
            currencyFullName: PropTypes.string,
            balanceAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
        cryptoID: PropTypes.shape({
            currencyShortName: PropTypes.string,
            currencyFullName: PropTypes.string,
            balanceAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
        fiatAmount: PropTypes.string,
        cryptoAmount: PropTypes.string,
    }).isRequired,
    quotationData: PropTypes.shape({
        price: PropTypes.number,
        error: PropTypes.any,
    }),
    loadingRate: PropTypes.bool,
};

BuyConfirmation.defaultProps = {
    quotationData: {},
    loadingRate: false,
};

export default BuyConfirmation;
